import Phaser from 'phaser';
import { Suit, Rank, SUIT_SYMBOLS, RANK_LABELS, CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS } from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { RENDER_SCALE } from '../renderScale';

// Scaled dimensions for high-resolution texture generation
const SCALED_CARD_WIDTH = CARD_WIDTH * RENDER_SCALE;
const SCALED_CARD_HEIGHT = CARD_HEIGHT * RENDER_SCALE;
const SCALED_CARD_RADIUS = CARD_RADIUS * RENDER_SCALE;

const SUIT_COLORS: Record<Suit, number> = {
  [Suit.HEARTS]: 0xe74c3c,
  [Suit.DIAMONDS]: 0xe74c3c,
  [Suit.CLUBS]: 0x2c3e50,
  [Suit.SPADES]: 0x2c3e50,
};

interface SymbolPosition {
  x: number;
  y: number;
  inverted?: boolean;
}

export class CardRenderer {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Generate all card textures
   */
  generateAllCardTextures(): void {
    for (const suit of Object.values(Suit)) {
      for (const rank of Object.values(Rank)) {
        if (typeof rank === 'number') {
          const key = `card-${suit}-${rank}`;
          // Skip if texture already exists (e.g., on hot reload)
          if (!this.scene.textures.exists(key)) {
            this.generateCardTexture(suit as Suit, rank as Rank, key);
          }
        }
      }
    }
    if (!this.scene.textures.exists('card-back')) {
      this.generateCardBackTexture('card-back');
    }
  }

  /**
   * Generate a single card texture at scaled resolution for crisp rendering
   */
  generateCardTexture(suit: Suit, rank: Rank, key: string): void {
    const tempKey = `_temp_${key}`;
    const graphics = this.scene.make.graphics({});
    const S = RENDER_SCALE; // Shorthand for scaling

    // Fill with border color first to eliminate white anti-aliased edges
    const borderColor = 0x333333;
    graphics.fillStyle(borderColor);
    graphics.fillRoundedRect(0, 0, SCALED_CARD_WIDTH, SCALED_CARD_HEIGHT, SCALED_CARD_RADIUS);

    // Card background (white) - inset by border width
    const borderWidth = 2 * S;
    graphics.fillStyle(0xffffff);
    graphics.fillRoundedRect(
      borderWidth,
      borderWidth,
      SCALED_CARD_WIDTH - borderWidth * 2,
      SCALED_CARD_HEIGHT - borderWidth * 2,
      SCALED_CARD_RADIUS - S
    );

    // Generate temporary texture from graphics
    graphics.generateTexture(tempKey, SCALED_CARD_WIDTH, SCALED_CARD_HEIGHT);
    graphics.destroy();

    // Now add text overlay using a render texture
    const rt = this.scene.make.renderTexture({ width: SCALED_CARD_WIDTH, height: SCALED_CARD_HEIGHT }, false);

    // Draw the card base
    rt.draw(tempKey, 0, 0);

    // Add rank and suit text
    const color = SUIT_COLORS[suit];
    const colorStr = `#${color.toString(16).padStart(6, '0')}`;
    const symbol = SUIT_SYMBOLS[suit];
    const label = RANK_LABELS[rank];

    // Top-left corner - use smaller font for "10" to prevent overlap with suit
    const isTen = label === '10';
    const rankFontSize = `${(isTen ? 11 : 14) * S}px`;
    const topRank = this.scene.make.text({
      x: 6 * S,
      y: 4 * S,
      text: label,
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: rankFontSize,
        color: colorStr,
        fontStyle: 'bold',
      },
    });

    const topSuit = this.scene.make.text({
      x: 6 * S,
      y: (isTen ? 16 : 18) * S,
      text: symbol,
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: `${14 * S}px`,
        color: colorStr,
      },
    });

    rt.draw(topRank, 6 * S, 4 * S);
    rt.draw(topSuit, 6 * S, (isTen ? 16 : 18) * S);

    // Bottom-right corner (rotated) - same font adjustments for "10"
    const bottomRank = this.scene.make.text({
      x: 0,
      y: 0,
      text: label,
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: rankFontSize,
        color: colorStr,
        fontStyle: 'bold',
      },
    });
    bottomRank.setOrigin(0.5, 0.5);
    bottomRank.setRotation(Math.PI);

    const bottomSuit = this.scene.make.text({
      x: 0,
      y: 0,
      text: symbol,
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: `${14 * S}px`,
        color: colorStr,
      },
    });
    bottomSuit.setOrigin(0.5, 0.5);
    bottomSuit.setRotation(Math.PI);

    rt.draw(bottomRank, SCALED_CARD_WIDTH - 14 * S, SCALED_CARD_HEIGHT - (isTen ? 10 : 12) * S);
    rt.draw(bottomSuit, SCALED_CARD_WIDTH - 14 * S, SCALED_CARD_HEIGHT - (isTen ? 22 : 26) * S);

    // Center symbol(s)
    this.drawCenterSymbols(rt, suit, rank, colorStr);

    // Save as final texture
    rt.saveTexture(key);

    // Cleanup temporary texture and objects
    this.scene.textures.remove(tempKey);
    topRank.destroy();
    topSuit.destroy();
    bottomRank.destroy();
    bottomSuit.destroy();
    rt.destroy();
  }

  private drawCenterSymbols(rt: Phaser.GameObjects.RenderTexture, suit: Suit, rank: Rank, colorStr: string): void {
    const symbol = SUIT_SYMBOLS[suit];
    const positions = this.getSymbolPositions(rank);
    const S = RENDER_SCALE;

    // For face cards, draw large center symbol
    if (rank >= Rank.JACK && rank <= Rank.KING) {
      const faceLabel = RANK_LABELS[rank];
      const faceText = this.scene.make.text({
        x: 0,
        y: 0,
        text: faceLabel,
        style: {
          fontFamily: 'Georgia, serif',
          fontSize: `${32 * S}px`,
          color: colorStr,
          fontStyle: 'bold',
        },
      });
      faceText.setOrigin(0.5, 0.5);
      rt.draw(faceText, SCALED_CARD_WIDTH / 2, SCALED_CARD_HEIGHT / 2);
      faceText.destroy();
      return;
    }

    // For Ace, draw large center symbol
    if (rank === Rank.ACE) {
      const aceSymbol = this.scene.make.text({
        x: 0,
        y: 0,
        text: symbol,
        style: {
          fontFamily: 'Arial, sans-serif',
          fontSize: `${40 * S}px`,
          color: colorStr,
        },
      });
      aceSymbol.setOrigin(0.5, 0.5);
      rt.draw(aceSymbol, SCALED_CARD_WIDTH / 2, SCALED_CARD_HEIGHT / 2);
      aceSymbol.destroy();
      return;
    }

    // For number cards, draw pip pattern
    for (const pos of positions) {
      const pip = this.scene.make.text({
        x: 0,
        y: 0,
        text: symbol,
        style: {
          fontFamily: 'Arial, sans-serif',
          fontSize: `${18 * S}px`,
          color: colorStr,
        },
      });
      pip.setOrigin(0.5, 0.5);
      if (pos.inverted) {
        pip.setRotation(Math.PI);
      }
      rt.draw(pip, SCALED_CARD_WIDTH * pos.x, SCALED_CARD_HEIGHT * pos.y);
      pip.destroy();
    }
  }

  private getSymbolPositions(rank: Rank): SymbolPosition[] {
    const layouts: Record<number, SymbolPosition[]> = {
      2: [
        { x: 0.5, y: 0.3 },
        { x: 0.5, y: 0.7, inverted: true },
      ],
      3: [
        { x: 0.5, y: 0.25 },
        { x: 0.5, y: 0.5 },
        { x: 0.5, y: 0.75, inverted: true },
      ],
      4: [
        { x: 0.3, y: 0.3 },
        { x: 0.7, y: 0.3 },
        { x: 0.3, y: 0.7, inverted: true },
        { x: 0.7, y: 0.7, inverted: true },
      ],
      5: [
        { x: 0.3, y: 0.25 },
        { x: 0.7, y: 0.25 },
        { x: 0.5, y: 0.5 },
        { x: 0.3, y: 0.75, inverted: true },
        { x: 0.7, y: 0.75, inverted: true },
      ],
      6: [
        { x: 0.3, y: 0.25 },
        { x: 0.7, y: 0.25 },
        { x: 0.3, y: 0.5 },
        { x: 0.7, y: 0.5 },
        { x: 0.3, y: 0.75, inverted: true },
        { x: 0.7, y: 0.75, inverted: true },
      ],
      7: [
        { x: 0.3, y: 0.2 },
        { x: 0.7, y: 0.2 },
        { x: 0.5, y: 0.35 },
        { x: 0.3, y: 0.5 },
        { x: 0.7, y: 0.5 },
        { x: 0.3, y: 0.8, inverted: true },
        { x: 0.7, y: 0.8, inverted: true },
      ],
      8: [
        { x: 0.3, y: 0.2 },
        { x: 0.7, y: 0.2 },
        { x: 0.5, y: 0.35 },
        { x: 0.3, y: 0.5 },
        { x: 0.7, y: 0.5 },
        { x: 0.5, y: 0.65, inverted: true },
        { x: 0.3, y: 0.8, inverted: true },
        { x: 0.7, y: 0.8, inverted: true },
      ],
      9: [
        { x: 0.3, y: 0.2 },
        { x: 0.7, y: 0.2 },
        { x: 0.3, y: 0.4 },
        { x: 0.7, y: 0.4 },
        { x: 0.5, y: 0.5 },
        { x: 0.3, y: 0.6, inverted: true },
        { x: 0.7, y: 0.6, inverted: true },
        { x: 0.3, y: 0.8, inverted: true },
        { x: 0.7, y: 0.8, inverted: true },
      ],
      10: [
        { x: 0.3, y: 0.15 },
        { x: 0.7, y: 0.15 },
        { x: 0.5, y: 0.28 },
        { x: 0.3, y: 0.4 },
        { x: 0.7, y: 0.4 },
        { x: 0.3, y: 0.6, inverted: true },
        { x: 0.7, y: 0.6, inverted: true },
        { x: 0.5, y: 0.72, inverted: true },
        { x: 0.3, y: 0.85, inverted: true },
        { x: 0.7, y: 0.85, inverted: true },
      ],
    };

    return layouts[rank] || [];
  }

  /**
   * Generate card back texture with Persian-style design at scaled resolution
   */
  generateCardBackTexture(key: string): void {
    const graphics = this.scene.make.graphics({});
    const S = RENDER_SCALE;
    const cx = SCALED_CARD_WIDTH / 2;
    const cy = SCALED_CARD_HEIGHT / 2;

    // Deep blue/teal background
    graphics.fillStyle(PERSIAN_THEME.colors.cardBack);
    graphics.fillRoundedRect(0, 0, SCALED_CARD_WIDTH, SCALED_CARD_HEIGHT, SCALED_CARD_RADIUS);

    // Gold outer border
    graphics.lineStyle(3 * S, PERSIAN_THEME.colors.gold, 1);
    graphics.strokeRoundedRect(3 * S, 3 * S, SCALED_CARD_WIDTH - 6 * S, SCALED_CARD_HEIGHT - 6 * S, SCALED_CARD_RADIUS);

    // Inner bronze border
    graphics.lineStyle(1 * S, PERSIAN_THEME.colors.bronze, 0.8);
    graphics.strokeRoundedRect(
      7 * S,
      7 * S,
      SCALED_CARD_WIDTH - 14 * S,
      SCALED_CARD_HEIGHT - 14 * S,
      SCALED_CARD_RADIUS - 2 * S
    );

    // Diamond lattice pattern
    graphics.lineStyle(1 * S, PERSIAN_THEME.colors.cardBackPattern, 0.3);
    const spacing = 10 * S;
    for (let x = spacing; x < SCALED_CARD_WIDTH - spacing; x += spacing) {
      for (let y = spacing; y < SCALED_CARD_HEIGHT - spacing; y += spacing) {
        const size = 3 * S;
        graphics.beginPath();
        graphics.moveTo(x, y - size);
        graphics.lineTo(x + size, y);
        graphics.lineTo(x, y + size);
        graphics.lineTo(x - size, y);
        graphics.closePath();
        graphics.strokePath();
      }
    }

    // Center medallion - outer ring
    graphics.lineStyle(2 * S, PERSIAN_THEME.colors.gold, 0.9);
    graphics.strokeCircle(cx, cy, 22 * S);
    graphics.lineStyle(1 * S, PERSIAN_THEME.colors.bronze, 0.7);
    graphics.strokeCircle(cx, cy, 18 * S);

    // 8-pointed star in center
    graphics.fillStyle(PERSIAN_THEME.colors.gold, 0.9);
    const starRadius = 12 * S;
    const innerRadius = 5 * S;
    graphics.beginPath();
    for (let i = 0; i < 8; i++) {
      const outerAngle = (i * Math.PI) / 4 - Math.PI / 2;
      const innerAngle = outerAngle + Math.PI / 8;
      const ox = cx + Math.cos(outerAngle) * starRadius;
      const oy = cy + Math.sin(outerAngle) * starRadius;
      const ix = cx + Math.cos(innerAngle) * innerRadius;
      const iy = cy + Math.sin(innerAngle) * innerRadius;
      if (i === 0) {
        graphics.moveTo(ox, oy);
      } else {
        graphics.lineTo(ox, oy);
      }
      graphics.lineTo(ix, iy);
    }
    graphics.closePath();
    graphics.fillPath();

    // Corner decorations (small diamonds)
    graphics.fillStyle(PERSIAN_THEME.colors.gold, 0.7);
    const corners = [
      { x: 15 * S, y: 20 * S },
      { x: SCALED_CARD_WIDTH - 15 * S, y: 20 * S },
      { x: 15 * S, y: SCALED_CARD_HEIGHT - 20 * S },
      { x: SCALED_CARD_WIDTH - 15 * S, y: SCALED_CARD_HEIGHT - 20 * S },
    ];
    for (const corner of corners) {
      const size = 4 * S;
      graphics.beginPath();
      graphics.moveTo(corner.x, corner.y - size);
      graphics.lineTo(corner.x + size, corner.y);
      graphics.lineTo(corner.x, corner.y + size);
      graphics.lineTo(corner.x - size, corner.y);
      graphics.closePath();
      graphics.fillPath();
    }

    graphics.generateTexture(key, SCALED_CARD_WIDTH, SCALED_CARD_HEIGHT);
    graphics.destroy();
  }
}
